// Settings Page - App preferences, theme, playback
import * as React from 'react';
import {
  Box,
  Typography,
  Stack,
  Divider,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  DarkMode,
  LightMode,
  AutoMode,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useSettings, ThemeMode, AccentColor, ACCENT_COLORS } from '../../hooks/useSettings';

export default function Settings() {
  const { settings, setSettings, themeMode, setThemeMode, setAccentColor, currentAccent } = useSettings();

  const themeModes: { value: ThemeMode; label: string; icon: React.ReactNode }[] = [
    { value: 'dark', label: 'Dark', icon: <DarkMode /> },
    { value: 'light', label: 'Light', icon: <LightMode /> },
    { value: 'system', label: 'System', icon: <AutoMode /> },
  ];

  const accentColors = Object.entries(ACCENT_COLORS) as [AccentColor, { hex: string; name: string }][];

  return (
    <Box sx={{ width: '100%', maxWidth: 700, mx: 'auto' }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={900} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon /> Settings
        </Typography>
      </Box>

      <Stack spacing={3}>
        {/* Appearance */}
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Appearance</Typography>

            {/* Theme Mode */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>Theme</Typography>
              <Stack direction="row" spacing={1.5}>
                {themeModes.map(mode => (
                  <Chip
                    key={mode.value}
                    label={mode.label}
                    icon={<Box sx={{ display: 'flex', '& > *': { fontSize: '1rem !important' } }}>{mode.icon}</Box>}
                    clickable
                    variant={themeMode === mode.value ? 'filled' : 'outlined'}
                    color={themeMode === mode.value ? 'primary' : 'default'}
                    onClick={() => setThemeMode(mode.value)}
                    sx={{ height: 40, fontWeight: 600 }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Accent Color */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                Accent Color <Chip label={currentAccent.name} size="small" sx={{ ml: 1 }} />
              </Typography>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                {accentColors.map(([key, val]) => (
                  <Box
                    key={key}
                    onClick={() => setAccentColor(key)}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      backgroundColor: val.hex,
                      cursor: 'pointer',
                      border: settings.accentColor === key ? '3px solid var(--anime-token-text-primary)' : '3px solid transparent',
                      transition: 'all 0.15s ease',
                      '&:hover': { transform: 'scale(1.1)' },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </CardContent>
        </Card>

        {/* Adult Content */}
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Content</Typography>

            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="subtitle2">Show Adult Content</Typography>
                  <Typography variant="caption" color="text.secondary">Display 18+ anime throughout the site</Typography>
                </Box>
                <Switch
                  checked={settings.showAdultContent}
                  onChange={(e) => setSettings({ showAdultContent: e.target.checked })}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="subtitle2">Blur Adult Covers</Typography>
                  <Typography variant="caption" color="text.secondary">Hide thumbnails of 18+ content behind a blur</Typography>
                </Box>
                <Switch
                  checked={settings.blurAdultCovers}
                  onChange={(e) => setSettings({ blurAdultCovers: e.target.checked })}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Playback */}
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Playback</Typography>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Auto Play</Typography>
                <Switch checked={settings.autoPlay} onChange={(e) => setSettings({ autoPlay: e.target.checked })} />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Auto Next Episode</Typography>
                <Switch checked={settings.autoNext} onChange={(e) => setSettings({ autoNext: e.target.checked })} />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Show Subtitles by Default</Typography>
                <Switch checked={settings.defaultSubtitle === 'show'} onChange={(e) => setSettings({ defaultSubtitle: e.target.checked ? 'show' : 'hide' })} />
              </Box>

              <FormControl fullWidth size="small">
                <InputLabel>Preferred Language</InputLabel>
                <Select
                  value={settings.preferredLanguage}
                  label="Preferred Language"
                  onChange={(e) => setSettings({ preferredLanguage: e.target.value as 'sub' | 'dub' | 'both' })}
                >
                  <MenuItem value="sub">Subtitled</MenuItem>
                  <MenuItem value="dub">Dubbed</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Playback Speed</InputLabel>
                <Select
                  value={settings.playbackSpeed}
                  label="Playback Speed"
                  onChange={(e) => setSettings({ playbackSpeed: Number(e.target.value) })}
                >
                  {[0.5, 0.75, 1, 1.25, 1.5, 2].map(s => (
                    <MenuItem key={s} value={s}>{s === 1 ? 'Normal' : `${s}x`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </CardContent>
        </Card>

        {/* Performance */}
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Performance</Typography>

            <FormControl fullWidth size="small">
              <InputLabel>Performance Mode</InputLabel>
              <Select
                value={settings.performanceMode}
                label="Performance Mode"
                onChange={(e) => setSettings({ performanceMode: e.target.value as 'auto' | 'high' | 'low' })}
              >
                <MenuItem value="auto">Auto (recommended)</MenuItem>
                <MenuItem value="high">High Quality (may lag on older devices)</MenuItem>
                <MenuItem value="low">Low Power (smoother on older devices)</MenuItem>
              </Select>
            </FormControl>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}